/**
 * Access to the Telegram Mini App ``initData`` for server-side HMAC auth.
 *
 * The backend ({@link apps.accounts.authentication}) expects the *raw* initData
 * query string in the ``Authorization`` header using the scheme:
 *
 *     Authorization: tma <raw initData>
 *
 * We never trust ``initDataUnsafe``/parsed fields for authorization — only the
 * raw signed string is forwarded; the server validates the HMAC.
 */
import {
  retrieveLaunchParams,
  retrieveRawInitData,
} from "@telegram-apps/sdk-react";

/** Authorization scheme expected by the backend DRF authentication class. */
export const TMA_AUTH_SCHEME = "tma" as const;

/**
 * Return the raw, signed ``initData`` query string, or ``null`` outside Telegram.
 *
 * This is the value to send for authorization. It is intentionally opaque on the
 * client — the server is the only party that validates it.
 */
export function getRawInitData(): string | null {
  try {
    const raw = retrieveRawInitData();
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Build the ``Authorization`` header value, or ``null`` when no initData exists.
 *
 * @example
 *   const value = getAuthHeaderValue(); // "tma query_id=...&user=...&hash=..."
 */
export function getAuthHeaderValue(): string | null {
  const raw = getRawInitData();
  return raw === null ? null : `${TMA_AUTH_SCHEME} ${raw}`;
}

/**
 * Return the ``start_param`` (deep-link payload, e.g. ``ref_<id>`` or an order
 * deep-link) from the launch parameters, or ``null`` when absent.
 *
 * For *authorization* still rely on {@link getRawInitData}; this helper is only
 * for read-only client routing decisions.
 */
export function getStartParam(): string | null {
  // 1) SDK launch params (v3: ``tgWebAppStartParam``).
  try {
    const params = retrieveLaunchParams() as { tgWebAppStartParam?: unknown };
    const sp = params.tgWebAppStartParam;
    if (typeof sp === "string" && sp.length > 0) {
      return sp;
    }
  } catch {
    /* fall through */
  }

  // 2) Most reliable: the signed initData carries ``start_param`` (the same string the
  //    server validates). Works regardless of SDK launch-param shape/timing.
  try {
    const raw = retrieveRawInitData();
    if (raw) {
      const sp = new URLSearchParams(raw).get("start_param");
      if (sp) {
        return sp;
      }
    }
  } catch {
    /* fall through */
  }

  // 3) Fallback: the raw launch hash (``#...&tgWebAppStartParam=order_3``).
  try {
    if (typeof window !== "undefined") {
      const hash = window.location.hash.replace(/^#/, "");
      const sp = new URLSearchParams(hash).get("tgWebAppStartParam");
      if (sp) {
        return sp;
      }
    }
  } catch {
    /* fall through */
  }

  return null;
}
